// Plan (epic) read-model projection — the wire shape `GET /v1/plans/:planId`
// returns (#511). A plan is the epic; its groups are the stacked-PR slices the
// planner authored (#123). This projection surfaces the epic → slice → task →
// PR chain so "which PR did this task produce" is one SQL join on the read path
// (the task↔PR link itself is the idempotent `tasks.pull_request_number`
// write-back from #543, not a new FK).
//
// Pure: imports only Zod + the domain's own enums (no I/O). The route layer
// consumes this as its response contract; `@developerz/db`'s `getPlanProjection`
// is typed to return it. Mirrors the `TaskEnvelope` ownership split (canonical
// shape here, db imports it).

import { z } from 'zod';
import { MergeDecisionSchema, PullRequestState, PullRequestStateSchema } from '../entities/issue';
import {
  OUTCOME_KIND_OPENS_PR,
  type OutcomeKind,
  TASK_DEP_SATISFIED_STATUSES,
  TaskHoldReasonSchema,
  TaskStatus,
  TaskStatusSchema,
} from '../entities/task';
import { HiveNoWorkSchema } from './hive-event/no-work';
import {
  PlanGoalStateSchema,
  PlanLegDeploySchema,
  PlanLegMergeHoldSchema,
  PlanProjectionEdgeSchema,
} from './plan-goal';
// Direct subpath, not the `./task-envelope` barrel: `DispatchChannel` (#4699)
// isn't re-exported there yet — add it when a cross-package consumer needs
// it. `DispatchedBy` itself IS in the barrel, but this file no longer
// references the type (only `via`, off one arm of it), so it isn't imported.
import {
  type DispatchChannel,
  TASK_PROBLEM_MAX,
  TaskLlmPreferenceSchema,
} from './task-envelope/envelope';

/**
 * Lifecycle of a plan group — the stacked-series state machine the dispatch
 * path drives. Canonical home; `@developerz/db`'s `PlanGroupRowStatus` aliases
 * this and the planner's `PlanGroupStatus` mirrors it. Text (Zod-validated at
 * read, no pgEnum) — same stance as `tasks`/`plan_groups`.
 *
 * CLOSED on what the system can WRITE, which is what makes this a gate rather
 * than a vocabulary: `pending` is the column default (`insertPlanGroups` leaves
 * `status` optional), `dispatched` is stamped at insert by the scout-plan ingest,
 * and `merged` is the one UPDATE (`markPlanGroupMergedByPr`). A fourth member —
 * `blocked` — lived here with NO writer until it was removed: the read gate
 * admitted a value nothing could produce, so a hand-written row would have
 * reached the wire instead of failing loudly, and the dashboard carried a chip
 * that could never render. `plan-groups.test.ts` enumerates the writers and
 * fails the build if this list outgrows them.
 *
 * NAMING THE DEAD TAIL IS NOT A FOURTH MEMBER, and this paragraph used to say it
 * would be — that a `blocked` status plus a writer on the PR-projection sink's
 * `closed` + `merged: false` branch was the landing. Prod, 2026-08-27, says that
 * writer could not have worked. Every plan-group writer resolves the group
 * through `tasks.pull_request_number`, and that column is NULL on **488 of the
 * 583** plan-group tasks — so a PR-webhook writer is structurally blind to five
 * out of six of the population, including all 529 groups that are dead because
 * their task `failed` or was `cancelled` without ever opening a pull request.
 * It would also have missed the ONE live closed-unmerged instance: plan
 * `run_1ebfa4bd931b45c49b325f7ddf36f019` group g1 DID open PR #1783 and that PR
 * closed unmerged, but the `linkTaskToPr` write-back never landed, so no query
 * keyed on the PR number can reach the group. A status is only as good as its
 * writers, and the writers cannot see most of the population.
 *
 * So the dead tail is DERIVED instead, by {@link planGroupDisposition}, from rows
 * that already exist and are already loaded by the projection. Same choice
 * `TaskStallKind` made for the same reason: *a reason that only exists once
 * something wrote it cannot explain a queue nothing has looked at.* It needs no
 * writer, no migration and no backfill — the 547 stranded groups in production
 * are named by the next read.
 */
export const PlanGroupStatus = {
  Pending: 'pending',
  Dispatched: 'dispatched',
  Merged: 'merged',
  /**
   * A held plan group (#2680) — authored tasks exist but are NOT YET in the
   * queue, awaiting a `plan approve` action. Stamped at insert by the scout-plan
   * ingest when the anchor scout task's envelope carries `require_approval: true`.
   * Flipped back to `dispatched` by the approve action (which also mints the
   * tasks it had held back). UNDISPATCHED, by construction — `claimNextReady`
   * never sees it, which is the point.
   */
  Held: 'held',
  /**
   * A cancelled plan group (#2696) — the operator pressed `plan cancel` on a
   * plan that still held undispatched slices, and the stop persisted the held
   * group's intent to never dispatch (a later `plan approve` cannot resurrect
   * them; `approveHeldPlanGroups` excludes `cancelled` from the SELECT AND from
   * the `already_approved` re-read). For a `dispatched` group it is the
   * TEAR-DOWN of its task row alongside the group's flip. Stamped at insert by
   * `cancelPlanTasks` (#2696) on both a fully-held plan and the held half of a
   * partially-held plan (option A: one stop, the whole plan).
   */
  Cancelled: 'cancelled',
} as const;
export const PlanGroupStatusSchema = z.enum(PlanGroupStatus);
export type PlanGroupStatus = z.infer<typeof PlanGroupStatusSchema>;

// ─── the dead tail, DERIVED (PLAN-2) ─────────────────────────────────────────
//
// `claimNextReady` (`@developerz/db` `queries/tasks.ts`) admits a downstream
// slice only when EVERY upstream group is `merged`. That gate is a safety
// property and this file does not widen it — `landed` below is exactly the set
// the gate admits, and nothing here writes a status. What was missing is that a
// group the gate will hold FOREVER looked identical to one still working:
// `dispatched`, with a NULL `hold_reason` on every task parked behind it.
//
// Prod 2026-08-27, all 583 plan groups, classified by the core below:
//
//   stalled / task_failed   529   the slice's task failed or was cancelled
//   landed                   32   group `merged` — the gate admits these
//   stalled / no_pr_link     18   task `completed`, no PR linked, so the ONE
//                                 writer of `merged` can never reach the group
//   in_flight                 4   genuinely still working
//
// `pr_closed_unmerged` carries zero rows TODAY only because the one instance of
// it lost its task↔PR link and presents as `no_pr_link`; it is producible by any
// linked plan PR closing unmerged, and the evidence (`pullRequest.state`) is
// already loaded by `getPlanProjection`. Leaving it out would file a provably
// dead group as `in_flight`, which is the direction this whole defect class runs.

// THE ONE-WAY DOOR, SETTLED (2026-09-01). Three ways out of the dead tail were on
// the table and only one is taken; the other two are refused here so nobody
// re-opens them by accident:
//
//   TAKEN      — fail the DEPENDENT. `cron/dead-plan-dependency.ts` (#2598) reads
//                this core and writes `TaskFailureKind.DependencyFailed` on the
//                held row. Live and still firing: 12 audited casualties on prod as
//                of 2026-09-01 — 9 `task_failed`, 3 `no_pr_link` — a floor on a
//                growing series, not a total.
//   REFUSED    — widen `claimNextReady` to release on `stalled`. The gate is a
//                SAFETY property: a downstream slice is authored against its
//                upstream's diff being on the base branch, and releasing it
//                because the upstream is dead builds the next slice on sand. That
//                is why `landed` here is exactly the set the gate admits and
//                `plan-groups.test.ts` fails the build if the two drift.
//   REFUSED    — store a terminal `plan_groups.status`. That is the `blocked`
//                mistake this file already paid for once: the only writer able to
//                reach the population resolves through `tasks.pull_request_number`,
//                which is NULL on five of six plan-group tasks, so the status
//                would be a vocabulary its writers cannot fill. A writer keyed
//                on `plan_groups.task_id` at the task's terminal transition
//                WOULD reach it — and would be falsified by `redispatchTask`,
//                which revives that same task id (#4235), so every revival
//                would need a second writer to reopen the group.
//
// What the taken door still owed, and this file now pays: a terminal write may
// only be made on a verdict nothing can revoke WITHOUT also reviving the casualty.
// See {@link PLAN_GROUP_STALL_FINALITY} and {@link stallFinalityPermitsFailing}.

/** Where a plan slice stands with respect to the gate that releases its dependents. */
export const PlanGroupDisposition = {
  /**
   * THE SLICE DELIVERED, AND THE GATE RELEASES ITS DEPENDENTS. Two ways in; with
   * {@link PlanGroupDisposition.NoWork} they are the whole set the gate admits
   * ({@link dispositionReleasesDependents}) — `plan-groups.test.ts` pins that set
   * against the gate's own source, so another way must arrive in both places:
   *
   *   1. `plan_groups.status = 'merged'` — written by `markPlanGroupMergedByPr`
   *      when the slice's pull request lands.
   *   2. The slice's task reached a satisfying terminal state and its
   *      `outcome_kind` opens no pull request ({@link OUTCOME_KIND_OPENS_PR}) —
   *      a `report` or `ops_action` slice that COMPLETED (#2949). Its group stays
   *      `dispatched` forever, because the one writer of `merged` resolves
   *      through `tasks.pull_request_number` and there is none; demanding that
   *      merge was what destroyed the implementation slice behind every
   *      investigation.
   *
   * A third way in — a slice whose run SETTLED NO WORK (#4235) — lived here
   * until it got its own member, {@link PlanGroupDisposition.NoWork}. It used to
   * say `landed` "because that is what a dependent needs to know — the slice
   * ahead of me is finished — not because a diff necessarily shipped". The
   * dependent is not the only reader: on plan `pex_fede7864…` (tesote/tesote.ai,
   * 2026-09-30) three `pr` slices settled no work, `plan get` read `landed` on
   * all three, and a person could not tell them from merged code. Release is a
   * question for {@link dispositionReleasesDependents}; this word now means a
   * slice delivered something.
   */
  Landed: 'landed',
  /**
   * THE SLICE'S CODING TEAM DISPROVED IT, AND THE GATE RELEASES ITS DEPENDENTS
   * exactly as it does for `landed` (#4235's release is unchanged). A slice whose
   * `outcome_kind` opens a pull request ({@link OUTCOME_KIND_OPENS_PR}) reached a
   * satisfying terminal state and its CURRENT run settled NO WORK
   * ({@link NO_WORK_SETTLED_EVENT}): the team read the checkout, proved the
   * request's premises false and completed with no pull request.
   *
   * ITS OWN MEMBER because nothing shipped. `landed` beside a slice whose premise
   * was false tells a person the planned change is on the base branch, and hides
   * the falsified premises — the one thing a re-plan needs.
   * {@link PlanProjectionGroupSchema.noWork} carries them.
   */
  NoWork: 'no_work',
  /** Still working, or waiting its turn. Nothing to report. */
  InFlight: 'in_flight',
  /** The gate will hold this group's dependents forever. {@link PlanGroupStallReason} says why. */
  Stalled: 'stalled',
} as const;
export const PlanGroupDispositionSchema = z.enum(PlanGroupDisposition);
export type PlanGroupDisposition = z.infer<typeof PlanGroupDispositionSchema>;

/**
 * Does the claim gate release this group's dependents? The ONE reading of a
 * disposition as a release, since `landed` stopped being the only answer that
 * releases — a `switch` with no default, so a new member fails the build here
 * until somebody says whether it releases. `plan-groups.test.ts` and
 * `plan-group-disposition-gate-parity.test.ts` pin its `true` set equal to the
 * gate's (`upstreamGroupUnmergedSql` / `taskDeliveredWithoutPrSql`).
 */
export function dispositionReleasesDependents(disposition: PlanGroupDisposition): boolean {
  switch (disposition) {
    case PlanGroupDisposition.Landed:
    case PlanGroupDisposition.NoWork:
      return true;
    case PlanGroupDisposition.InFlight:
    case PlanGroupDisposition.Stalled:
      return false;
  }
}

/** Why a slice can no longer reach `merged`. Non-null exactly when the disposition is `stalled`. */
export const PlanGroupStallReason = {
  /**
   * The slice's task reached a terminal failure. Permanent: a retry mints a NEW
   * task id, so this group's `task_id` still points at the dead row. Unblocking
   * it means re-authoring or re-dispatching the slice.
   */
  TaskFailed: 'task_failed',
  /**
   * A slice that WAS supposed to open a pull request finished without one linked:
   * `markPlanGroupMergedByPr` resolves the group through
   * `tasks.pull_request_number` and so can never reach it, whatever happens to
   * the PR afterwards.
   *
   * NARROWED BY #2949, and the narrowing is the fix. It used to cover a second
   * cause — a task that delivers no PR at all (`report` / `ops_action`) — and
   * that cell is no longer a stall at all: such a slice DELIVERED, the widened
   * gate releases its dependents, and {@link PlanGroupDisposition.Landed} is its
   * answer. What survives here is the genuine deadlock, and it keeps killing.
   *
   * NARROWED AGAIN BY #4235: a `pr` slice whose run settled NO WORK is `landed`
   * too — it completed with no PR on purpose. On prod 2026-09-16 that was 182 of
   * the 205 completed `pr` groups this reason covered.
   *
   * WIDENED BACK IN BY #5137, on purpose: an `ops_action` `completed` with no
   * PR (still `report`/`ops_action`'s own shape) is `no_pr_link` again when its
   * CURRENT run carries {@link OPS_ACTION_REFUSED_EVENT} — the production ops
   * gate refused it and escalated rather than running it. That is not the
   * genuine delivery #2949 carved out; it is a task that finished having done
   * nothing, awaiting a human's `OpsApproval`.
   *
   * THE RECOVERY PATH, STATED HONESTLY: falling through to this existing arm
   * rather than a fourth reason gives the group the SAME MECHANICAL door a lost
   * PR link already has — `redispatchTask` on the same task id re-pends
   * whatever the sweep failed underneath it, whatever box picks the redispatch
   * up. That redispatch clears the block ONLY if the box that claims it is
   * NON-PRODUCTION (`decideOutcome`, `apps/runner/src/modes/worker/outcome.ts`,
   * runs an `ops_action` freely when `!input.production`) or the task's own
   * `outcome_kind` no longer routes it through the production gate. Landing on
   * a production box again reproduces the same refusal, because "once
   * approved" NAMES A MINTER THIS REPO DOES NOT HAVE: `packages/mcp/src/army/ops.ts`
   * states outright that no path in this codebase ever writes an `OpsApproval`
   * — the field is read (`apps/runner`) and refused (this MCP verb) but minted
   * by neither. Until that minter exists, a redispatch is a bet on which box
   * claims it, not a guaranteed unblock — a known gap, not a solved one.
   */
  NoPrLink: 'no_pr_link',
  /**
   * The linked pull request closed WITHOUT merging. The stall is real and
   * REVOCABLE: `closed` is not a terminal PR state, so this group can still
   * reach `merged` with no hand-written write anywhere.
   *
   * The enumeration behind that, both arms held by a test rather than asserted
   * here — the claim is about writers, so it is pinned where the writers live:
   *   - `applyPrCore` (`@developerz/db` `queries/pull-requests.ts`) guards
   *     exactly ONE state against downgrade, `merged`, so a later delivery still
   *     writes over `closed`. `pull-requests.integration.test.ts` walks a live
   *     row `closed → open → merged`.
   *   - EVERY pull-request action but `opened` folds `state` through that writer,
   *     `reopened` included, so the reopen needs no lane of its own.
   *     `projection-sink.test.ts` pins the `reopened` fold; the `closed` +
   *     `merged: true` after it flips the group through `markPlanGroupMerged`,
   *     which the same file already pins.
   *
   * {@link PLAN_GROUP_STALL_FINALITY} stores the grade, and it is why the repair
   * sweep leaves this one waiting instead of failing its dependents.
   */
  PrClosedUnmerged: 'pr_closed_unmerged',
  /**
   * An operator pressed `plan cancel` on a plan that still held this group
   * (#2696). Permanent: `approveHeldPlanGroups` excludes `cancelled` from its
   * held-group SELECT and its `already_approved` re-read, so a later approve
   * cannot resurrect it. The dependent sweep acts on this — a dependent of a
   * cancelled group is a casualty with this reason, no need to wait for any
   * PR evidence that will never arrive.
   */
  Cancelled: 'cancelled',
} as const;
export const PlanGroupStallReasonSchema = z.enum(PlanGroupStallReason);
export type PlanGroupStallReason = z.infer<typeof PlanGroupStallReasonSchema>;

/**
 * Can a writer this system ALREADY RUNS undo this stall? The property a lane
 * taking a PERMANENT action has to read before it takes one.
 *
 * WHY IT IS A SEPARATE AXIS FROM THE REASON. `planGroupDisposition` answers "is
 * this group dead", which is a READ. `cron/dead-plan-dependency.ts` turns that
 * read into a TERMINAL write — every downstream slice `failed` with
 * `TaskFailureKind.PlanDependencyFailed`. Those are only the same question when
 * no writer can revoke the verdict without reviving the casualty too, and one of
 * the four reasons below provably can (`pr_closed_unmerged`). `pr-projection-reconcile.ts` already
 * states the contract this table makes mechanical: *this lane may leave a plan
 * waiting, but it may not make one look dead* — waiting is the recoverable half.
 *
 * THE SAME `Record` DISCIPLINE AS {@link PLAN_GROUP_TASK_DISPOSITION}, for the
 * same reason: a fifth stall reason FAILS THE BUILD until somebody names the
 * writer that can or cannot undo it. A `Set` of revocable reasons, or a check
 * inside the sweep, would let a new member default silently into "permanent" —
 * which is the direction that kills work.
 *
 * EACH ANSWER CITES ITS WRITER, because "irrevocable" is a universal and a
 * universal with no enumeration is a claim nobody checked.
 */
export const PlanGroupStallFinality = {
  /** No writer this system runs can move this group back toward `merged`. */
  Irrevocable: 'irrevocable',
  /**
   * Only an operator REDISPATCH of the group's own task can revive it — and that
   * same redispatch re-pends every dependent the sweep failed on its account
   * (`redispatchTask`, `@developerz/db` `queries/tasks/operator.ts`, re-pends the
   * `failed` + `plan_dependency_failed` rows downstream of the revived task's
   * group). So failing the dependents is not final: the one door back into the
   * upstream carries them through it.
   */
  RepairedOnRevival: 'repaired_on_revival',
  /** A writer that already exists can still carry this group to `merged`, and repairs nothing it passes. */
  Revocable: 'revocable',
} as const;
export const PlanGroupStallFinalitySchema = z.enum(PlanGroupStallFinality);
export type PlanGroupStallFinality = z.infer<typeof PlanGroupStallFinalitySchema>;

/**
 * May the dead-plan-dependency sweep write a TERMINAL status onto a dependent of
 * a group stalled at this grade? The ONE answer the sweep partitions on — a
 * `switch` with no default, so a fourth grade fails the build here.
 *
 * `Revocable` is the only refusal: its reviving writer (a PR `reopened` fold) re-pends
 * nothing, so a casualty written now would stay dead after its upstream landed.
 */
export function stallFinalityPermitsFailing(finality: PlanGroupStallFinality): boolean {
  switch (finality) {
    case PlanGroupStallFinality.Irrevocable:
    case PlanGroupStallFinality.RepairedOnRevival:
      return true;
    case PlanGroupStallFinality.Revocable:
      return false;
  }
}

/**
 * WHICH STALLS ARE PERMANENT — total over {@link PlanGroupStallReason}, and the
 * ONE input `cron/dead-plan-dependency.ts` reads before it fails a dependent.
 *
 *   - `task_failed` — REPAIRED ON REVIVAL. This used to read IRREVOCABLE because
 *     "a retry mints a NEW task id", and that was false: `failOrRetryTask`
 *     re-pends the SAME id while budget remains, and past it `redispatchTask`
 *     (`queries/tasks/operator.ts`) revives the SAME terminal row that
 *     `plan_groups.task_id` still points at. Prod 2026-09-16: 16 upstreams died
 *     of `provider_quota_exhausted`, a transient, and one was pending again while
 *     its dependents stayed dead. The sweep still fails the dependents (a failed
 *     upstream parks them forever otherwise) because that redispatch re-pends them.
 *   - `cancelled` — IRREVOCABLE. `approveHeldPlanGroups` excludes `cancelled`
 *     from its held-group SELECT **and** from its `already_approved` re-read, so
 *     a later `plan approve` cannot resurrect the group. Both exclusions, named,
 *     because one of them alone would leave a way back in.
 *   - `no_pr_link` — REPAIRED ON REVIVAL. No EVENT can revive it, and that is an
 *     ENUMERATION of EVERY writer of `tasks.pull_request_number` rather than a
 *     claim that there is one (there are three, and the older wording named only
 *     the first). Test fixtures are IN the list, not filtered out of it: a reader
 *     checking this claim greps the column and finds all three, and a list that
 *     silently dropped one leaves them unable to tell a deliberate exclusion from
 *     a forgotten writer:
 *       1. `linkTaskToPr` (`@developerz/db` `queries/tasks/pr-lane.ts`) — the
 *          only writer that touches an EXISTING row's column, CAS-guarded on
 *          `is null` (set-once) and called from exactly one site, the marker arm
 *          of the projection sink's `pull_request.opened` handling
 *          (`apps/worker/src/babysit/marker-link.ts`). For a stalled slice
 *          that delivery is already spent, and a LATER pull request quoting the
 *          same task writes nothing — it comes back as the `second_pr` verdict.
 *       2. `ensureBabysitWatchTask` (`queries/tasks/babysit.ts`) — an INSERT that
 *          stamps the column at BIRTH on a `pr_babysit_watch` anchor of its own,
 *          under a deterministic (repo, pr) id and `onConflictDoNothing`. It
 *          mints a different row and updates none, so it can never fill THIS
 *          task's null. That is why the grade survives the second writer:
 *          "exactly one writer" implied the property by accident; "no writer
 *          fills an existing null except a spent delivery" IS the property.
 *       3. The dev seed fixtures (`packages/db/src/seeds/fleet.ts` and
 *          `packages/db/src/seeds/dev/fleet-fixtures.ts`) — also at INSERT, on
 *          rows they mint themselves, and the one of them that upserts sets
 *          `repo_full_name` and nothing else on conflict. Listed because the
 *          property is about the COLUMN and not about production code: a fixture
 *          that filled an existing null would falsify this grade in every
 *          developer's database and in every integration test that reads it,
 *          which is precisely where the sweep's behaviour is checked before it
 *          ships. It does not, so the property holds everywhere the code runs.
 *
 *     #5137's REFUSED-ops_action cell needs no fourth writer added to this list:
 *     a `report`/`ops_action` task never gets a `pull_request_number` at all
 *     (the same fact #2949's Landed arm relies on), refused or not, so the
 *     enumeration above already covers it — there is no column any of these
 *     three writers could fill for it either. But the row IS `completed`
 *     (`refuse` returns rather than throwing), and `redispatchTask` revives a
 *     `completed` row exactly like any other terminal one, with the same
 *     re-pend of the dependents. (The "slice that never had a PR" half of this
 *     reason is gone since #2949, and the "settled no work" half since #4235:
 *     both are `landed`.)
 *   - `pr_closed_unmerged` — **REVOCABLE**, and it is the reason this table
 *     exists. `applyPrCore` folds `state` from EVERY pull-request action and
 *     guards only against downgrading a `merged` row, so `closed` is the one
 *     settled state a later delivery still moves: `reopened` writes `open`, and
 *     the `closed` + `merged: true` after it flips the group through
 *     `markPlanGroupMerged`. Both arms are pinned by a test — see
 *     {@link PlanGroupStallReason.PrClosedUnmerged}, which names them — because
 *     this is the ONE grade whose flip to `Irrevocable` a future edit to those
 *     writers could earn silently. Sweeping it would kill every downstream slice of a
 *     plan whose upstream then landed — the one direction
 *     {@link planGroupDisposition}'s `pullRequestState === Merged` arm already
 *     refuses for the already-merged case, arriving through the door that arm
 *     does not cover: not merged ALREADY, merged LATER.
 *
 * WHAT THE SWEEP DOES WITH A GRADE is {@link stallFinalityPermitsFailing}, not a
 * comparison of its own: it fails on `Irrevocable` and `RepairedOnRevival`, and
 * leaves a `Revocable` stall waiting. Splitting `RepairedOnRevival` out of
 * `Irrevocable` (#4235) moved no casualty — both fail — it made the table stop
 * asserting a finality the redispatch writer falsifies, and it names the contract
 * that writer must keep for the sweep's write to be safe.
 */
export const PLAN_GROUP_STALL_FINALITY: Record<PlanGroupStallReason, PlanGroupStallFinality> = {
  [PlanGroupStallReason.TaskFailed]: PlanGroupStallFinality.RepairedOnRevival,
  [PlanGroupStallReason.NoPrLink]: PlanGroupStallFinality.RepairedOnRevival,
  [PlanGroupStallReason.Cancelled]: PlanGroupStallFinality.Irrevocable,
  [PlanGroupStallReason.PrClosedUnmerged]: PlanGroupStallFinality.Revocable,
};

/** What one task status says about its plan group, before the PR evidence is read. */
export const PlanGroupTaskDisposition = {
  /** Terminal failure — no retry of THIS row advances the group. */
  Dead: 'dead',
  /** In flight: the slice is queued, running, or in the babysit lane. */
  Working: 'working',
  /** The task's own PR merged. Whether the GROUP landed is `plan_groups.status`'s answer. */
  Landed: 'landed',
  /** The run is over; whether the group can still advance is decided by the PR link. */
  AwaitingPrLink: 'awaiting_pr_link',
} as const;
export type PlanGroupTaskDisposition =
  (typeof PlanGroupTaskDisposition)[keyof typeof PlanGroupTaskDisposition];

/**
 * WHAT EACH TASK STATUS MEANS FOR ITS PLAN GROUP — total over {@link TaskStatus},
 * and that totality is the guard, not a nicety. A `Record` (not a `Map`, a `Set`
 * or a switch with a `default`) for the same reason `ORIGIN_OUTCOME_KIND` is one:
 * adding a task status FAILS THE BUILD until somebody says what it does to a
 * group. The 529 groups sitting `dispatched` behind a `failed` task are exactly
 * what an omitted answer costs — `failed` and `cancelled` were reachable from the
 * day the column existed and no plan-group code path had ever considered them.
 */
export const PLAN_GROUP_TASK_DISPOSITION: Record<TaskStatus, PlanGroupTaskDisposition> = {
  [TaskStatus.Pending]: PlanGroupTaskDisposition.Working,
  [TaskStatus.Assigned]: PlanGroupTaskDisposition.Working,
  [TaskStatus.InProgress]: PlanGroupTaskDisposition.Working,
  // The one status whose meaning is not decidable from the status alone: the run
  // finished, and whether anything can still carry the group to `merged` depends
  // on whether a pull request is linked to it.
  [TaskStatus.Completed]: PlanGroupTaskDisposition.AwaitingPrLink,
  [TaskStatus.AwaitingCi]: PlanGroupTaskDisposition.Working,
  [TaskStatus.AwaitingReview]: PlanGroupTaskDisposition.Working,
  [TaskStatus.Mergeable]: PlanGroupTaskDisposition.Working,
  [TaskStatus.Merged]: PlanGroupTaskDisposition.Landed,
  [TaskStatus.Failed]: PlanGroupTaskDisposition.Dead,
  [TaskStatus.Cancelled]: PlanGroupTaskDisposition.Dead,
  // #6747: paused by a person, back on Resume — still the group's work.
  [TaskStatus.Paused]: PlanGroupTaskDisposition.Working,
};

/** The evidence {@link planGroupDisposition} weighs — all of it already on the projection read. */
export type PlanGroupDispositionInput = {
  /** The stored group lifecycle — what `claimNextReady` actually reads. */
  groupStatus: PlanGroupStatus;
  /**
   * The dispatched task, or null for a group no pass has dispatched yet.
   *
   * `outcome_kind` IS read, and reading it is #2949's fix. This docblock used to
   * say the opposite — that a completed `report` slice being `no_pr_link` was
   * "right about the DAG", that splitting it would mean a stall reason with zero
   * producers because "prod 2026-08-27 has no plan-group task whose
   * `outcome_kind` is anything but `pr`", and that the real fix was "a
   * `claimNextReady` predicate, not a second label here".
   *
   * The last of those was right and is what shipped: the GATE widened
   * ({@link OUTCOME_KIND_OPENS_PR}, `upstreamGroupUnmergedSql`). The premise did
   * not survive — prod 2026-09-02 had five `dispatched` groups holding a non-PR
   * task, three `report` and two `ops_action`, each permanently blocking its
   * subtree. And no second LABEL was added: the cell moves to `landed`, which the
   * gate now genuinely admits, so the two stay one rule.
   */
  task: {
    status: TaskStatus;
    /** `tasks.pull_request_number` — the column every plan-group writer resolves through. */
    pullRequestNumber: number | null;
    /**
     * Did the task's CURRENT run (`tasks.run_id`) settle NO WORK — a
     * {@link NO_WORK_SETTLED_EVENT} `status` run event on that run (#4235)? Loaded
     * by `taskSettledNoWorkSql` (`@developerz/db`), the same fragment the claim
     * gate reads. REQUIRED for the reason `outcomeKind` is: a reader that could
     * omit it would re-create the no-work kill on whichever read forgot.
     */
    noWorkSettled: boolean;
    /**
     * `tasks.outcome_kind` — whether this slice was ever going to open a pull
     * request. REQUIRED, not optional-defaulting-to `pr`: the column is
     * `not null default 'pr'` so every row has one, and a reader that could omit
     * it would silently re-create the exact bug (#2949) on whichever read forgot.
     */
    outcomeKind: OutcomeKind;
    /**
     * Did the task's CURRENT run (`tasks.run_id`) carry an
     * {@link OPS_ACTION_REFUSED_EVENT} `status` run event (#5137) — a production
     * `ops_action` that {@link decideOutcome} (`apps/runner`) refused to run for
     * want of a fresh `OpsApproval` and escalated instead? Loaded by
     * `taskOpsActionRefusedSql` (`@developerz/db`), the same fragment
     * `taskDeliveredWithoutPrSql` reads. REQUIRED for the reason `noWorkSettled`
     * is: a refused ops action still completes the task (`refuse` returns without
     * throwing), so without this flag it would satisfy arm 2 below exactly like a
     * genuine delivery — the completion state and the deliverable read identical
     * on every column BUT this one.
     */
    opsActionRefused: boolean;
  } | null;
  /** The linked PR's mirrored state, or null when none is linked / mirrored. */
  pullRequestState: PullRequestState | null;
};

/**
 * The `task_run_events.payload.event` of the `status` row a `pr` run emits when its
 * coding team settled the task as NO WORK (`docs/idea/hive.md` rule 12) — a success
 * that completes the task with no pull request. The box-mothership contract: the
 * runner's `settleNoWork` (`apps/runner` `modes/worker/outcome.ts`) writes it, and
 * `taskSettledNoWorkSql` (`@developerz/db`) reads it to release a plan group's
 * dependents. Canonical HERE so the writer and the reader cannot spell it twice.
 *
 * DURABLE: `status` is not in `RAW_LOG_EVENT_KINDS`, so the 72h payload redaction
 * never NULLs it.
 */
export const NO_WORK_SETTLED_EVENT = 'no_work_settled' as const;

/**
 * The `task_run_events.payload.event` of the `status` row `refuse`
 * (`apps/runner` `modes/worker/outcome.ts`) emits when a PRODUCTION `ops_action`
 * has no fresh `OpsApproval` and is refused before it runs (#5137). The run
 * still finishes and the task still lands `completed` — `refuse` returns rather
 * than throwing — so `taskOpsActionRefusedSql` (`@developerz/db`) reads this row
 * to keep that completion from satisfying a plan dependent the way a genuine
 * report/ops_action delivery does. Canonical HERE, the same discipline
 * {@link NO_WORK_SETTLED_EVENT} holds, so the writer and the reader cannot spell
 * it twice.
 *
 * `refuse` ALSO emits a `human_message` `ops_action_needs_approval` escalation —
 * that one is NOT this event and must never be read for this fact:
 * `human_message` IS in `RAW_LOG_EVENT_KINDS` (the 72h raw-log window), so a
 * gate keyed on it would read satisfied once the escalation ages out — three
 * days after the refusal, with no new evidence that the ops action ever ran.
 * `status` is not in that allowlist, so THIS event stays durable for as long as
 * the row does.
 */
export const OPS_ACTION_REFUSED_EVENT = 'ops_action_refused' as const;

/**
 * Classify one plan slice. PURE — the projection's only judgement, and it writes
 * nothing: a stalled group keeps its stored `status`, so the claim gate is
 * untouched and no dependent is released by being explained.
 */
export function planGroupDisposition(input: PlanGroupDispositionInput): {
  disposition: PlanGroupDisposition;
  stallReason: PlanGroupStallReason | null;
} {
  const inFlight = {
    disposition: PlanGroupDisposition.InFlight,
    stallReason: null,
  } as const;
  const stalled = (stallReason: PlanGroupStallReason) =>
    ({ disposition: PlanGroupDisposition.Stalled, stallReason }) as const;

  // `merged` is the gate's own answer and outranks every other signal. A group
  // whose PR landed is landed even if its task row later failed a follow-up.
  if (input.groupStatus === PlanGroupStatus.Merged) {
    return { disposition: PlanGroupDisposition.Landed, stallReason: null };
  }
  // `cancelled` (#2696) — the operator pressed `plan cancel` on a held or
  // partially-held plan, and the store stamped the group's intent to never
  // advance. A `cancelled` group with a null task (the held branch of the
  // cancel) is the same answer as a `cancelled` group that ever held a task:
  // the gate will never release any dependent. Distinct from `task_failed`
  // because the action was OPERATOR-INITIATED, not run-shaped — the dead-plan-
  // dependency sweep carries this reason and a held group's `task === null`
  // would otherwise read as `in_flight`, indistinguishable from the slice the
  // sweep should leave alone (the undispatched gate at the bottom).
  if (input.groupStatus === PlanGroupStatus.Cancelled) {
    return stalled(PlanGroupStallReason.Cancelled);
  }
  // An undispatched group is not stalled — nothing has been asked of it yet.
  if (input.task === null) return inFlight;

  // A SLICE THAT OPENS NO PULL REQUEST BY DESIGN, AND HAS DELIVERED (#2949).
  // `markPlanGroupMergedByPr` resolves the group through
  // `tasks.pull_request_number`, so a `report` / `ops_action` slice sits
  // `dispatched` forever — demanding that merge held every downstream slice until
  // the repair sweep killed it (developerz-ai/wurk#470: group 0 completed, group 1
  // dead 6.5 minutes later).
  //
  // THE PREDICATE IS THE GATE'S, TERM FOR TERM — `outcome_kind` opens no PR
  // AND the task is in {@link TASK_DEP_SATISFIED_STATUSES} — and it is asked
  // HERE, at the top, for exactly that reason. `upstreamGroupUnmergedSql` cannot see PR
  // state or task disposition, so an arm placed below those would answer
  // differently from the gate on cells the gate reaches: a `report` slice at
  // `TaskStatus.Merged` would fall to the redelivery-window arm and read
  // `in_flight` while the gate released it, and one carrying a closed PR would
  // read `stalled` for the same reason. `plan-groups.test.ts` pins the two sets
  // equal; this placement is what makes that pin true rather than lucky.
  //
  // NO ELSE-BRANCH. Everything this does not claim falls through unchanged, so a
  // `report` slice that FAILED still reaches the `Dead` arm below and is
  // `task_failed` — "opens no pull request" must never become "always satisfies".
  //
  // A `pr` slice whose current run SETTLED NO WORK (#4235) is the same cell by a
  // second door: it promised a pull request only if there was work, proved there
  // was none, and completed. The gate's disjunct is `taskSettledNoWorkSql`, term
  // for term.
  //
  // `!opsActionRefused` IS ANDED ONTO THE WHOLE DISJUNCTION (#5137), never
  // into the no-PR-kind door alone — the SAME shape `taskDeliveredWithoutPrSql`
  // (`@developerz/db` `queries/tasks/gates.ts`) renders in SQL. A refused
  // `ops_action` still lands `completed` with no PR, indistinguishable from a
  // genuine delivery on every OTHER column. Gating only the first door agrees
  // with this shape everywhere a writer can reach today and disagrees on the
  // one cell it cannot yet — `plan-group-disposition-gate-parity.test.ts`
  // proves the two spellings identical over the FULL cross product.
  // A PR-opening slice enters only through the no-work door, so it shipped nothing.
  // Keyed on the KIND, not the flag: the listing reads the flag only for PR-opening
  // kinds (`lazyTaskNoWorkSettledSql`), and a `report` delivered whatever it says.
  if (
    (!OUTCOME_KIND_OPENS_PR[input.task.outcomeKind] || input.task.noWorkSettled) &&
    !input.task.opsActionRefused &&
    (TASK_DEP_SATISFIED_STATUSES as readonly TaskStatus[]).includes(input.task.status)
  ) {
    return {
      disposition: OUTCOME_KIND_OPENS_PR[input.task.outcomeKind]
        ? PlanGroupDisposition.NoWork
        : PlanGroupDisposition.Landed,
      stallReason: null,
    };
  }

  // A LINKED PR THAT MERGED IS NOT A DEAD TAIL, whatever the task row says — the
  // same rule the `groupStatus === 'merged'` arm above applies, read off the other
  // evidence source. The diff is on the base branch, and `markPlanGroupMergedByPr`
  // resolves the group through THIS `pull_request_number`, so the group's flip is
  // OWED rather than impossible. It is the identical redelivery window the
  // `TaskStatus.Merged` arm at the bottom names; the core carried that reasoning
  // for the task row and not for the PR row, which is the whole defect.
  //
  // ORDERED ABOVE `Dead`, and that ordering is the fix: `selectDeadPlanDependents`
  // ACTS on a stall, so classifying this cell `stalled(task_failed)` FAILS every
  // downstream slice of a plan whose upstream already shipped — killing a dependent
  // whose dependency landed, which is the one direction that lane may not fail in.
  // Reachable by design from `apps/worker`'s `cron/pr-projection-reconcile.ts`: it
  // repairs a lost `closed` delivery by writing `pull_requests.state = 'merged'` and
  // replaying `retireTasksForClosedPr` (which lands `cancelled`), while deliberately
  // NOT flipping the plan group — leaving exactly this shape.
  //
  // `in_flight` and never `landed`: the gate reads `plan_groups.status` and this
  // writes nothing, so `landed` stays exactly the set `claimNextReady` admits
  // (`plan-groups.test.ts` fails if it does not) and no dependent is released here.
  // The honest cost is that the window does not converge on its own — the reconcile
  // lane's refusal to flip the group is what would close it, and that is recorded
  // in its module doc rather than smuggled in here. Prod 2026-08-29: ZERO rows in
  // this cell, against 26 of 90 linked plan-group PRs sitting `task = failed, pr =
  // merged` where only the group flip having landed keeps them out of it.
  if (input.pullRequestState === PullRequestState.Merged) return inFlight;

  const byStatus = PLAN_GROUP_TASK_DISPOSITION[input.task.status];
  if (byStatus === PlanGroupTaskDisposition.Dead) {
    return stalled(PlanGroupStallReason.TaskFailed);
  }
  // Terminal PR evidence outranks a mid-flight task status: a closed-unmerged PR
  // ends the group whatever the task row still claims about itself.
  if (input.pullRequestState === PullRequestState.Closed) {
    return stalled(PlanGroupStallReason.PrClosedUnmerged);
  }
  if (byStatus === PlanGroupTaskDisposition.AwaitingPrLink) {
    // A `pr` slice whose run is over with no PR linked ⇒ nothing can ever flip
    // this group: it opened one and the `linkTaskToPr` write-back never landed.
    // That deadlock is real and the sweep is right to act on it. (The slice that
    // opens no PR BY DESIGN, #2949, and the one that settled no work, #4235,
    // never reach here — both are answered at the top.)
    if (input.task.pullRequestNumber === null) return stalled(PlanGroupStallReason.NoPrLink);
    return inFlight;
  }
  // `Landed` (the task's PR merged) without the group flipped is a REDELIVERY
  // WINDOW, not a stall: `retireSettledPrLane` and `markPlanGroupMerged` both fire
  // from the SAME `closed` + `merged: true` delivery, so the two cannot durably
  // disagree. Zero rows carried it on prod 2026-08-27.
  return inFlight;
}

/**
 * GitHub tri-state mergeability (`mergeable` | `conflicting` | `unknown`),
 * matching `pull_requests.mergeable`. Nullable: a not-yet-mirrored PR carries
 * no value. Mirrors the `Mergeability` type on `BabysitObservation`, as a Zod
 * schema here so the wire contract validates it.
 */
export const MergeabilitySchema = z.enum(['mergeable', 'conflicting', 'unknown']);

/**
 * The dispatched task for a group — null until the group is dispatched
 * (`plan_groups.task_id` is stamped on the `dispatched` flip). `status` is the
 * fleet task lifecycle (`tasks.status`); `pullRequestNumber` is the PR the task
 * opened (#543 write-back), null until known.
 */
export const PlanProjectionTaskSchema = z.object({
  id: z.string().min(1),
  status: TaskStatusSchema,
  pullRequestNumber: z.number().int().positive().nullable(),
});
export type PlanProjectionTask = z.infer<typeof PlanProjectionTaskSchema>;

/**
 * The PR a group's task produced, mirrored from GitHub — null when the task
 * hasn't opened one yet OR the PR row hasn't been mirrored into `pull_requests`.
 * `number` is the GitHub PR number (the join key on `tasks.pull_request_number`),
 * scoped to the group's repo. `mergeDecision` is the babysit/auto-merge verdict
 * (`auto` | `ask` | `block`), null while undecided.
 */
export const PlanProjectionPullRequestSchema = z.object({
  number: z.number().int().positive(),
  state: PullRequestStateSchema,
  title: z.string(),
  mergeable: MergeabilitySchema.nullable(),
  mergeDecision: MergeDecisionSchema.nullable(),
  /**
   * GitHub's `merge_commit_sha` — the commit the merge produced, NOT `head.sha` (#3702).
   * Null while the PR is open, and null on rows merged before the column existed. This
   * is the deploy-proof join key: a release deploys the merge commit, so that is the sha
   * `deploy_verdicts` records.
   */
  mergeCommitSha: z.string().nullable(),
});
export type PlanProjectionPullRequest = z.infer<typeof PlanProjectionPullRequestSchema>;

/**
 * WHY THIS SLICE'S TASK IS NOT MOVING, as a dispatcher pass stamped it
 * (`tasks.hold_reason`), plus the one pointer the reason cannot carry.
 *
 * THE DEFECT IT CLOSES, measured on prod 2026-09-11. `markRepoNotOnboardedHolds`
 * has been writing `repo_not_onboarded` onto held rows the whole time and the
 * projection read none of it — `plan get` rendered `status: dispatched`,
 * `disposition: in_flight`, and NOTHING ELSE, so a slice held on a gate said
 * exactly what a slice actively being worked says. On `developerz-ai/ggrts` five
 * tasks had waited since 2026-08-31 while the repo's `dz/onboarding` pull request
 * (#19) sat open from 2026-09-08, and neither the reason nor the pull request
 * appeared on any read a maintainer had.
 *
 * NOT A SECOND {@link PlanGroupStallReasonSchema}, and the line between them is
 * the point: a stall reason says the slice can NEVER reach `merged`, and is
 * DERIVED from dead evidence; a hold says it has not STARTED yet and names
 * something a human can still do. A held slice's disposition stays `in_flight`,
 * because nothing about it is dead.
 *
 * `pullRequestNumber` is the ONBOARDING pull request — never this slice's own
 * (that is {@link PlanProjectionGroupSchema.pullRequest}, the PR its task
 * produced). It is non-null only for {@link TaskHoldReason.RepoNotOnboarded},
 * because that is the only hold whose remedy is a merge; every other member is
 * cleared by capacity, a key, a sibling or an upstream group, none of which has a
 * pull request to name. Resolved through the repo's SETUP-origin task row
 * (`tasks.pull_request_number`), which is the durable pointer — `pull_requests`
 * carries no `head_ref`, so the `dz/onboarding` branch is not queryable from the
 * mirror at all.
 */
export const PlanGroupHoldSchema = z.object({
  /** The stamped `tasks.hold_reason` — the machine half. */
  reason: TaskHoldReasonSchema,
  /** The onboarding pull request whose merge lifts the hold; null when none is open. */
  pullRequestNumber: z.number().int().positive().nullable(),
});
export type PlanGroupHold = z.infer<typeof PlanGroupHoldSchema>;

/**
 * WHAT A `no_work` SLICE'S TEAM DISPROVED — the evidence behind
 * {@link PlanGroupDisposition.NoWork}, read off the `team_manifest` `status` event
 * of the task's CURRENT run (its `no_work` field, `HiveNoWorkSchema`), which the
 * runner emits before it settles (`apps/runner` `team/session/run.ts`). Durable:
 * `status` is outside the 72h raw-log redaction.
 *
 * `runId` is the pointer (`task run_tail` reads that run). `falsified` and `facts`
 * are the team's own lists, each non-empty whenever the manifest carried them —
 * `HiveNoWorkSchema` refuses an empty one at write — so EMPTY here means one
 * thing only: the run's manifest carries no readable `no_work` (a run from before
 * the field existed). The disposition does not depend on them; the settlement
 * event decides it.
 */
export const PlanGroupNoWorkSchema = z.object({
  runId: z.string().min(1),
  falsified: z.array(z.string()),
  facts: z.array(z.string()),
});
export type PlanGroupNoWork = z.infer<typeof PlanGroupNoWorkSchema>;

/**
 * Parse a `team_manifest` payload's `no_work` value into the plan read's evidence.
 * Anything {@link HiveNoWorkSchema} refuses (absent, null, malformed) yields empty
 * lists — see {@link PlanGroupNoWorkSchema} for what empty means.
 */
export function planGroupNoWorkOf(runId: string, manifestNoWork: unknown): PlanGroupNoWork {
  const parsed = HiveNoWorkSchema.safeParse(manifestNoWork);
  return parsed.success
    ? { runId, falsified: parsed.data.falsified, facts: parsed.data.facts }
    : { runId, falsified: [], facts: [] };
}

/**
 * One slice of the epic: its plan-order position, lifecycle, DAG dependencies,
 * and the dispatched task + PR (each independently nullable). `dependsOn` lists
 * sibling group ids that must reach `merged` first.
 */
export const PlanProjectionGroupSchema = z.object({
  groupId: z.string().min(1),
  /** 0-based plan order — stable against `g10 > g2` lexical traps. */
  position: z.number().int().nonnegative(),
  title: z.string(),
  /**
   * THIS SLICE'S OWN REPO (`plan_groups.repo_full_name`) — one repo, one box, one
   * PR. A plan may span several (arc 101 slice 05), so it is a fact about the
   * GROUP and there is no plan-level answer to fall back on:
   * {@link PlanSummarySchema.repoFullName} is the LEAD slice's repo, which is a
   * different question and wrong for every other slice.
   *
   * REQUIRED, not optional, for the reason `heldOn` is: this schema is the read
   * boundary (`getPlanProjection` parses every row through it), so a producer that
   * cannot say which repo a slice lives in fails HERE, naming the group, instead
   * of answering `undefined` to a reader that has to render it. An optional field
   * on the one shape whose point is that the repo is per-group would reintroduce
   * the collapse this slice removed, one layer up.
   */
  repoFullName: z.string().min(1),
  status: PlanGroupStatusSchema,
  /**
   * The upstream group ids, `edges[].upstream` in edge order. KEPT beside `edges` (slice 12)
   * for the reason {@link PlanSummarySchema.repoFullNames} sits beside `repoFullName`: this
   * is the `GET /v1/plans/:planId` wire, read by dashboard pods that roll gradually and by
   * the worker's plan readers (mirror, stacked-PR links, revise), none of which needs more
   * than the ids. Both fields come off ONE read of `plan_group_edges`.
   */
  dependsOn: z.array(z.string()),
  /**
   * EVERY edge into this leg (plan 101 D2), as stated (`release`, `environment`, `hold`) and
   * whether it has released now — see {@link PlanProjectionEdgeSchema}. Sorted by upstream.
   */
  edges: z.array(PlanProjectionEdgeSchema),
  /**
   * Where this leg's merge is live, one entry per environment — see
   * {@link PlanLegDeploySchema} and {@link planLegDeploys}. `[]` before the merge.
   */
  deploy: z.array(PlanLegDeploySchema),
  /**
   * The `hold: merge` edges whose release this leg's MERGE still waits on (D4,
   * `upstream_leg_unreleased`) — `mergeHeldOf(edges)`, never a second judgement. A leg held
   * at START says so on `heldOn` (`upstream_group_unmerged` / `upstream_leg_undeployed`).
   */
  mergeHeld: z.array(PlanLegMergeHoldSchema),
  task: PlanProjectionTaskSchema.nullable(),
  pullRequest: PlanProjectionPullRequestSchema.nullable(),
  /**
   * DERIVED from the three fields above by {@link planGroupDisposition} — never
   * stored, so it cannot go stale and needed no backfill for the 547 groups that
   * were already stranded when it shipped.
   */
  disposition: PlanGroupDispositionSchema,
  /** Why the slice can no longer reach `merged`. Non-null exactly when `disposition` is `stalled`. */
  stallReason: PlanGroupStallReasonSchema.nullable(),
  /**
   * What the slice's team disproved. Non-null exactly when `disposition` is
   * `no_work` — see {@link PlanGroupNoWorkSchema}.
   */
  noWork: PlanGroupNoWorkSchema.nullable(),
  /**
   * Why the slice's task has not STARTED, read off `tasks.hold_reason` — null when
   * the row carries no hold (it is running, terminal, or merely queued). See
   * {@link PlanGroupHoldSchema} for why it is not a stall reason and why the pull
   * request it names is the onboarding one, never the slice's own.
   */
  heldOn: PlanGroupHoldSchema.nullable(),
  /**
   * A live task this HELD group will link on `plan approve` instead of minting one
   * (`plan_groups.adopts_task_id`, written by `plan execute`). Present only when set:
   * every other producer of this shape has no adoption to report.
   */
  adoptsTaskId: z.string().min(1).optional(),
});
export type PlanProjectionGroup = z.infer<typeof PlanProjectionGroupSchema>;

/**
 * Lifecycle of the PLAN itself — the `plans` row, not its groups (arc 101 doc 08,
 * "`plans` as a real row"). A plan used to be an implied grouping of `plan_groups`
 * rows sharing a `plan_id`, so there was nowhere to say "this epic was stopped":
 * `plan cancel` on a fully-dispatched plan flips its TASKS and stamps no group
 * (`cancelPlanTasks` only stamps groups with no task to cancel through), and the
 * projection then showed `dispatched` slices with cancelled tasks and no
 * plan-level answer at all.
 *
 * CLOSED on what the system can WRITE, the same stance {@link PlanGroupStatus}
 * takes and for the same reason — a member with no writer is a value the read gate
 * admits and no code path produces. Each member and its writer:
 *
 * | member | writer |
 * |---|---|
 * | `open` | the `plans.status` column DEFAULT, landed by `insertPlanGroups`'s parent-row upsert (`@developerz/db`) — a plan is BORN with its first groups, in one transaction |
 * | `cancelled` | `cancelPlanTasks` (`@developerz/db` `queries/tasks.ts`), the `plan cancel` verb's transaction |
 * | `done` | `markPlanGroupMergedByPr` (`@developerz/db` `queries/plan-groups/write.ts`) in the SAME transaction that flips the plan's LAST group satisfied (`planGroupSatisfiedSql`: merged, a completed `report`/`ops_action` slice (and not refused on its current run), or a `pr` slice whose run settled no work — #5137), and `reconcilePlansDone` (same file, same predicate) from the worker's `cron.dead_dependency` tick — the one-shot for the pre-existing twelve (#4018), and now the ROUTINE closer (#5137) for a plan whose last satisfier is not a merge, since only `markPlanGroupMergedByPr` rides a merge webhook |
 * | `ended` | `reconcilePlansEnded` (`@developerz/db` `queries/plan-groups/ended.ts`) from the same `cron.dead_dependency` tick, for a plan whose every slice is satisfied or settled terminal (`failed`/`cancelled`/`merged`, not superseded) and not every slice satisfied (plan 101 R3); back to `open` by `reopenRevivedPlans` (same file) when a redispatch revives one of its slices |
 *
 * `plan-groups.test.ts` enumerates them and fails the build if this list outgrows
 * them.
 *
 * `done` is a STORED, SYSTEM-WRITTEN fact, reversing the arc 101 doc 08 stance
 * that completion is derived (owner ruling 2026-09-14, #4018). Prod on that day:
 * 12 of 879 plans had every group `merged` and the plan row still `open` — a query
 * could not answer "which epics landed" without re-deriving it over `plan_groups`
 * every time. What made the old stance right was its reason — a member NOBODY
 * writes — and `done` has a writer, the same CAS that lands the last group. Both
 * terminal members are terminal: the cancel CAS is `status = 'open'`, so a landed
 * plan cannot be re-labelled stopped, and the done predicate is `status = 'open'`,
 * so a stopped plan whose groups all happened to merge stays `cancelled`.
 */
export const PlanStatus = {
  /** Live. The column default — every plan is born here. */
  Open: 'open',
  /** Stopped by `plan cancel`. Idempotent by CAS on `open`; a re-cancel writes nothing. */
  Cancelled: 'cancelled',
  /**
   * Every group SATISFIED (`planGroupSatisfiedSql`: merged, a completed
   * `report`/`ops_action` slice (and not refused on its current run), or a
   * `pr` slice whose run settled no work — #4018, widened #5137). Written by
   * the system — the last group's merge
   * transaction, or the `cron.dead_dependency` reconcile tick (the routine
   * closer for a plan whose last satisfier is not a merge) — never by hand.
   * Terminal: `plan cancel` on a done plan writes nothing, AND NO SLICE CAN
   * RESTART: every writer that revives a terminal task locks every plan it
   * belongs to first and refuses by name when one is `done` (#5348) —
   * `redispatchTask` for a single task, and `regroupPlanGroups`'s cascade-casualty
   * revival for more than one in the same transaction (`@developerz/db`
   * `queries/tasks/redispatch-plan-guard.ts`, whose docblock enumerates every
   * OTHER writer that moves a task toward `pending` and states why none of them
   * can revive a terminal one). The three satisfying arms above are each
   * revocable (a redispatch re-pends the row) and none has any OTHER writer
   * that could put the plan back together once one is undone from underneath it.
   */
  Done: 'done',
  /**
   * Every slice ENDED and not every slice LANDED (plan 101 R3, owner-delegated ruling
   * 2026-10-08): each group is satisfied, cancelled, or linked to a task that settled
   * terminal (`failed`, `cancelled`, `merged`) and was not superseded — so nothing in the
   * plan can move without a person. Prod 2026-10-01 (M19): 1,243 of 1,379 `open` plans
   * had no live task; `open` claimed work that no longer existed.
   *
   * Written by the system — `reconcilePlansEnded` on the `cron.dead_dependency` tick, the
   * same lock discipline as `reconcilePlansDone` — and only GOING FORWARD: a plan whose
   * tasks all settled before `PLAN_ENDED_LOOKBACK_HOURS` is left as it was (no backfill).
   *
   * NOT terminal the way `done` is. A redispatch that revives one of its slices puts the
   * plan back to `open` in the same transaction (`reopenRevivedPlans`), because the
   * operator's retry is exactly the person the plan was waiting on. Every other writer
   * that requires `open` (cancel, revise, regroup, unlink, append) treats it as closed.
   */
  Ended: 'ended',
} as const;
export const PlanStatusSchema = z.enum(PlanStatus);
export type PlanStatus = z.infer<typeof PlanStatusSchema>;

/**
 * WHO STARTED THE PLAN — `user` | `system`, stamped on the `plans` row at mint
 * (#4340). Text + Zod at read, no pgEnum: the house idiom `plans.status`
 * already uses.
 *
 * THE VOCABULARY CHANGED MEANING TWICE WITHOUT CHANGING ITS NAME. It used to
 * be `DispatchedBy`'s `kind` — `user` meaning a USER-tier token (REST or MCP)
 * pulled the trigger. That was never personhood: the box's own installation
 * token authenticates as `kind: 'user'` too
 * (`apps/api/src/deps/army-audit-user-id.test.ts:51`). #4699 moved it to
 * {@link DispatchChannel} alone — `user` meant `via === 'session'` ONLY, a
 * live dashboard cookie, on the theory that it was the one channel a person
 * and an agent cannot both hold. That theory's own trip condition ("a CLI
 * user asking why their plan is missing") fired within hours of deploy: a
 * person on the CLI dispatches through `via: 'pat'` too, and #4699 read that
 * as `system` unconditionally. #4727 corrects it: `user` now means
 * `via === 'session'` **OR** (`via === 'pat'` AND the dispatch names a bound
 * `user_id`) — a person on a credential we can name, not merely a person in
 * a browser. `system` still covers everything else: an ingest lane, an
 * UNBOUND `via: 'pat'` dispatch (the agent-PAT case this classification
 * exists to exclude — `claude epic1888 ralph loop …`, minted by direct DB
 * insert with no `user_id`), and a `kind: 'user'` dispatch with no `via` at
 * all (minted before that field existed — channel unknown, never widened by
 * a bound id it might still carry).
 *
 * STORED, not derived: the only read-time trail would be
 * `tasks.run_id = plans.id`, and `reviveSet()` writes `runId: null` on every
 * re-run (18 of 816 scout plans already unlinkable, prod 2026-09-17), with no
 * index on the join. The ONE writer is `insertPlanGroups`'s parent-row upsert;
 * each widening's own migration backfills the evidence that survives (the
 * anchor task's envelope AND its `dispatched_by_user_id` column, the
 * scaffold's own task row) — see #4699's migration for why every historical
 * row lacking `via` re-stamps to `system`, and #4727's for why history can
 * still be recovered THERE from `kind: 'user'` plus a bound
 * `dispatched_by_user_id` alone, with no `via` conjunct: `via` has been
 * stamped ZERO TIMES in production (measured 2026-09-22), so every
 * historical `kind: 'user'` envelope predates that field entirely and a
 * `via`-gated backfill would restamp nothing. That coarser predicate is
 * ONLY for the historical backfill — `planInitiatorOf` above still requires
 * `via === 'pat'` at read/write time, because a mint AFTER #4699 shipped
 * always carries a channel.
 *
 * ABSENT EVIDENCE IS `system`, never a claimed human — the same rule under
 * both vocabularies: an unlinkable row, a missing anchor envelope, a
 * `kind: 'user'` dispatch with no `via` — all say the machine started it.
 */
export const PlanInitiator = {
  User: 'user',
  System: 'system',
} as const;
export const PlanInitiatorSchema = z.enum(PlanInitiator);
export type PlanInitiator = z.infer<typeof PlanInitiatorSchema>;

/**
 * The evidence {@link planInitiatorOf} convicts on — the anchor's
 * {@link DispatchChannel} (#4699) PLUS its bound user id (#4727), read
 * together because the corrected rule needs both: `via === 'pat'` alone no
 * longer decides anything by itself.
 *
 * `via`, absent (`null` OR `undefined`) when there is none to name: a
 * `system`-kind dispatch (no channel exists), or a `kind: 'user'` dispatch
 * minted before `via` existed (a legacy envelope — channel unknown, not "no
 * channel"). BOTH spellings of absent are accepted on purpose: the
 * envelope's own `dispatched_by.via` is an optional field (`undefined` when
 * missing — `setup-envelope.ts`'s call site), while `FleetEventOwner
 * .dispatchedByVia` (`@developerz/db`) is a SQL JSONB extraction, which
 * reads a missing key as `null`, never `undefined` — the scout-plan ingest's
 * call site. A type narrow enough to demand one spelling would force every
 * SQL-sourced caller to coalesce null to undefined for no semantic gain
 * (`=== 'session'` treats them identically), so the widen belongs here, once,
 * rather than at each such call site.
 *
 * `boundUserId` — the `users.id` behind the dispatch, or `null` when the
 * dispatch names none (an unbound token, a `system`-kind dispatch, an
 * absent/legacy anchor). REQUIRED and explicitly nullable, never optional —
 * this repo's posture for exactly this reason: an optional field a caller
 * forgets to supply would compile, pass an unwatched suite, and silently
 * fall back to "no evidence" for a lane that actually had a bound id to
 * hand. `via === 'session'` never reads this field (a session already
 * convicts on its own); it is consulted only when `via === 'pat'`.
 *
 * Was a discriminated union over TWO mint lanes before #4699 — `dispatched_by`
 * (scout anchor / scaffold) and `raw_task_source` (triage). The
 * `raw_task_source` arm is GONE, not merely dead: `RawTaskSource.Api`'s own
 * docblock defines it as "a human OR AN AGENT filing work directly", so it
 * could never actually evidence a person, and a branch that can no longer
 * convict is worse left in than removed. The triage mint lane calls this
 * function NOT AT ALL — an ask records no channel, so it stamps through the
 * sibling {@link askPlanInitiatorOf} (#5103), on the ask's own
 * `filed_by_user_id` rather than on a `via` this evidence shape demands. The
 * scout anchor and the scaffold both still hand this the SAME shape: one
 * lane's worth of evidence, not two.
 */
export type PlanInitiatorEvidence = {
  readonly via: DispatchChannel | null | undefined;
  readonly boundUserId: string | null;
};

/**
 * THE ONE mapping from CHANNEL-bearing mint-lane evidence to
 * {@link PlanInitiator} — pure and TOTAL, so no call site re-decides the rule
 * (a second spelling would drift: a lane reading `dispatched_by.kind` instead
 * of `.via` compiles, passes an unwatched suite, and convicts a
 * PAT-dispatched, user_id-bound task as person-started forever — or the
 * opposite drift, a lane that widens on `boundUserId` alone regardless of
 * channel, which would readmit the over-count #4699 existed to close). Its
 * sibling {@link askPlanInitiatorOf} is the one mapping for the ONE lane that
 * records no channel (raw-task triage): that lane does not fake a `via` to
 * route through here, because an ask carries none.
 *
 * `via === 'session'` convicts unconditionally — a live dashboard cookie is
 * the one channel a person and an agent cannot both hold. `via === 'pat'`
 * convicts ONLY when `boundUserId` is present (#4727) — a person on the CLI
 * with their own credential is still a person, but the unbound agent PATs
 * (`claude epic1888 ralph loop …`, minted by direct DB insert with no
 * `user_id`) are exactly what the original over-count was, and they must
 * stay excluded. Everything else — a `system`-kind dispatch (no channel
 * exists), or an ABSENT `via` (channel unknown, never widened by a bound id
 * it might still carry) — is `system`.
 */
export function planInitiatorOf(evidence: PlanInitiatorEvidence): PlanInitiator {
  if (evidence.via === 'session') return PlanInitiator.User;
  if (evidence.via === 'pat' && evidence.boundUserId !== null) return PlanInitiator.User;
  return PlanInitiator.System;
}

/**
 * THE ONE mapping from an ASK'S FILER to the {@link PlanInitiator} PAIR —
 * {@link planInitiatorOf}'s sibling for the one mint lane that records no
 * channel: raw-task triage (#5103). Pure and TOTAL, the same doctrine — no
 * call site re-decides the rule.
 *
 * WHY THE FILER ALONE IS SUFFICIENT EVIDENCE. `raw_tasks.filed_by_user_id` is
 * written only at the THREE human doors: `ask file`
 * (`packages/mcp/src/army/ask.ts`, `caller.userId ?? null`), `POST /v1/asks`
 * (`apps/api/src/routes/asks.ts`, `principal.userId ?? null`) and the
 * multi-repo `plan_start` (`apps/api/src/deps/plan-start.ts`,
 * `dispatchedBy.user_id`). A principal carries a `userId` only for a
 * dashboard session or a PAT bound to a user — "a user-less machine
 * credential (box connection token, legacy PAT) still resolves to no actor"
 * (`apps/api/src/middleware/auth.ts`). So a NON-NULL filer means exactly a
 * session or a bound PAT: the two cases {@link planInitiatorOf} convicts.
 * Every machine inlet (webhook, janitor, scout, blocker answer) and every
 * unbound-PAT ask files NULL and stays `system`.
 *
 * It does NOT route through {@link planInitiatorOf} with a faked `via`: an
 * ask records no channel, and inventing one would state an evidence shape no
 * door ever saw.
 *
 * RETURNS THE PAIR — `{ initiatedBy, initiatedByUserId }` — so the two
 * `plans` columns cannot be stamped from different evidence: a caller spreads
 * this into the write, and neither "user, named by nobody" nor "system,
 * naming somebody" is spellable.
 */
export function askPlanInitiatorOf(filedByUserId: string | null): {
  initiatedBy: PlanInitiator;
  initiatedByUserId: string | null;
} {
  if (filedByUserId === null) {
    return { initiatedBy: PlanInitiator.System, initiatedByUserId: null };
  }
  return { initiatedBy: PlanInitiator.User, initiatedByUserId: filedByUserId };
}

/**
 * WHICH MINT LANE AUTHORED THE PLAN — `scout` | `custom`. `plans.id` is
 * caller-minted, and the PREFIX IS THE MINT LANE (`packages/db/src/schema/plans.ts`):
 *
 * | prefix | lane |
 * |---|---|
 * | `run_<32 hex>` | a scout's `plan_authored` (`mintRunId`, apps/runner/src/run-scope.ts) |
 * | `rtk_<hex>` | raw-task triage — the ask's own id |
 * | `tsk_<hex>` | a scaffold (its plan id IS its task id) |
 *
 * `custom` is every non-scout lane, not a value anyone writes. `rawTaskId`
 * CANNOT discriminate (a scaffold's is null too), and {@link PlanInitiator} is a
 * different axis — WHO started it (`user` | `system`), not which lane minted it.
 *
 * DERIVED AT READ, never stored — the {@link planGroupDisposition} stance: no
 * column, no migration, no backfill. The id is the row's own immutable key, so
 * the derivation cannot go stale the way a copied column can.
 */
export const PlanSource = {
  Scout: 'scout',
  Custom: 'custom',
} as const;
export const PlanSourceSchema = z.enum(PlanSource);
export type PlanSource = z.infer<typeof PlanSourceSchema>;

/**
 * THE ONE mapping from a plan id to {@link PlanSource} — pure and TOTAL, the
 * `planInitiatorOf` doctrine: one derivation, so no call site re-decides the
 * rule (a second spelling is the drift seam). `run_` is the scout lane's mint
 * prefix; every other mint lane — `rtk_` triage, `tsk_` scaffold, any
 * caller-minted id — is `custom`.
 */
export function planSourceOf(planId: string): PlanSource {
  return planId.startsWith('run_') ? PlanSource.Scout : PlanSource.Custom;
}

/**
 * WHO STARTED THE PLAN, as a nameable person (#4477) — `login` and `avatar`
 * only, the same login-only precedent `findTaskDispatchAttribution`
 * (`@developerz/db`) already set for public PR-body provenance: nothing more
 * of a person is surfaced here than is already served to any member with
 * `read:account` (`GET /v1/org/members`).
 *
 * `avatar` is EXPLICITLY NULLABLE, never optional — `users.avatar` is a
 * nullable column (GitHub may withhold one, and a first-party-OIDC user has
 * none at all), so a producer that could omit the field would collapse "no
 * avatar" and "avatar not looked up" into the same wire shape.
 */
export const PlanOwnerSchema = z.object({
  login: z.string().min(1),
  avatar: z.string().nullable(),
});
export type PlanOwner = z.infer<typeof PlanOwnerSchema>;

/**
 * The epic read-model — the plan id (caller-minted, shared by every group) plus
 * its groups in plan order. Returned by `GET /v1/plans/:planId`; absent/foreign
 * plans surface as a 404 at the route (the query returns null when no rows
 * match the tenant fence).
 *
 * `status` and `rawTaskId` come from the `plans` ROW, which is what the read is
 * anchored on now — the projection's tenant fence is an inner join to it, so a
 * group can only ever be projected under the plan of its own account (the
 * composite FK `plan_groups (plan_id, account_id) → plans (id, account_id)` is
 * what makes the two fences one).
 */
export const PlanProjectionSchema = z.object({
  planId: z.string().min(1),
  /** The plan row's own lifecycle — see {@link PlanStatus}. NOT derived from the groups. */
  status: PlanStatusSchema,
  /**
   * The `raw_tasks` ask this plan was authored from, or null when it was not
   * authored from one (every scout-authored plan today). The column that makes
   * "one big ask becomes MANY plans" expressible — the hierarchy arc 101 doc 08
   * names, and the reason the raw-task cutover needed this table first.
   */
  rawTaskId: z.string().min(1).nullable(),
  /**
   * Who started the plan — see {@link PlanInitiator}. REQUIRED, never optional:
   * this schema is the read boundary, so a producer that cannot say fails HERE
   * naming the plan instead of answering `undefined` to a reader that renders it.
   */
  initiatedBy: PlanInitiatorSchema,
  /**
   * Which mint lane authored the plan — see {@link PlanSource}. DERIVED from the
   * plan's own id at read (`planSourceOf`), never stored; REQUIRED, never
   * optional, matching `initiatedBy` — a producer that cannot say fails HERE
   * naming the plan instead of answering `undefined` to a reader that renders it.
   */
  source: PlanSourceSchema,
  /**
   * The org member who started the plan (#4477), or null when no evidence
   * names one — an absent anchor, a system-dispatched lane, or a token with no
   * bound user, none of which is a claim to invent. REQUIRED, never optional:
   * this schema is the read boundary, so a resolver that cannot say fails HERE
   * naming the plan instead of answering `undefined` to a reader that renders
   * it — the same posture `initiatedBy` and `source` already take on this
   * object, for the same reason.
   */
  owner: PlanOwnerSchema.nullable(),
  /**
   * The epic's model (#7012, `plans.llm`): the pin each slice claimed from now on runs
   * on unless it pins its own — or null, "Automatic", when tier routing chooses. REQUIRED,
   * never optional, for the reason `owner` is: a reader renders "Automatic" off null,
   * and must never render it off a producer that forgot to say.
   */
  llm: TaskLlmPreferenceSchema.nullable(),
  /**
   * Where the goal stands (slice 12) — {@link planGoalState} over `status` and the groups. A
   * read-model LABEL, never stored: the stored truth is `status`.
   */
  goalState: PlanGoalStateSchema,
  groups: z.array(PlanProjectionGroupSchema),
});
export type PlanProjection = z.infer<typeof PlanProjectionSchema>;

/**
 * WHY a plan has stopped — the plan-level answer the Custom plans row renders
 * (#5102), derived from the plan's non-landed groups by `planStopReason`
 * (`./plan-stop-reason.ts`): a `stalled` group contributes its
 * {@link PlanGroupStallReason}, and an IN-FLIGHT group whose DISPATCHED task
 * carries a `hold_reason` (`tasks.hold_reason`, see {@link PlanGroupHoldSchema})
 * contributes that task's {@link TaskHoldReasonSchema} member. Two
 * vocabularies, never a third — the discriminator says which one `reason`
 * speaks.
 *
 * `kind: 'held'` NAMES A DISPATCHED TASK'S HOLD, NOT {@link PlanGroupStatus.Held}.
 * A `PlanGroupStatus.Held` group has no task at all (see
 * {@link PlanSummarySchema.heldCount}), so it can never carry a `hold_reason`
 * and never contributes here — a plan whose only non-landed slices are
 * `PlanGroupStatus.Held` (waiting on `plan approve`) answers `stopReason: null`.
 * That plan has not stopped; it is waiting on a person to release work that
 * already exists.
 *
 * The name is "stop", not "stall": {@link PlanProjectionGroupSchema}'s
 * `stallReason` is the GROUP-level, terminal-only reason, and a plan-level
 * `stallReason` would be a second meaning for one name — a plan whose every
 * dispatched slice is hold-blocked has stopped without a single stalled group.
 *
 * LIVES IN THIS FILE, not beside its derivation, to avoid an import cycle: it
 * needs {@link PlanGroupStallReasonSchema} (same file) and
 * {@link TaskHoldReasonSchema} (already imported here). `plan-stop-reason.ts`
 * imports from this module, never the other way round.
 */
export const PlanStopReasonSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('held'),
    reason: TaskHoldReasonSchema,
    groupCount: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('stalled'),
    reason: PlanGroupStallReasonSchema,
    groupCount: z.number().int().positive(),
  }),
]);
export type PlanStopReason = z.infer<typeof PlanStopReasonSchema>;

/**
 * Plan (epic) index row — the light summary a `plan list` returns, one per plan
 * the account owns. Deliberately NOT the full {@link PlanProjection}: the list is
 * a scan surface (which epics exist, how far along), and the group→task→PR chain
 * loads on demand via `plan get`. A plan carries no title column (the id is
 * caller-minted), so `leadTitle` is the lowest-position slice's title — the
 * plan's scannable headline. `mergedCount`/`groupCount` is the progress fraction.
 * Timestamps are pre-ISO'd, matching the projection's Date-free wire stance.
 *
 * THE COUNTS COME FROM {@link planGroupDisposition}, the same core the full
 * projection classifies with (`listPlansByAccount` loads the group + task + PR
 * rows and tallies in TypeScript). They are deliberately NOT re-expressed as a
 * SQL `CASE`: two spellings of one question are a drift seam with nothing to
 * catch them, and the question here is the one prod answered 529-of-583
 * `stalled` on 2026-08-27 — every one of which the old `merged / total` fraction
 * rendered as work still in motion.
 */
export const PlanSummarySchema = z.object({
  planId: z.string().min(1),
  /**
   * The plan row's own lifecycle — see {@link PlanStatus}. NOT a roll-up of the
   * group counts below: those say how the WORK is going, this says whether the
   * epic is still meant to happen at all.
   */
  status: PlanStatusSchema,
  /** The `raw_tasks` ask this plan was authored from — see {@link PlanProjectionSchema}. */
  rawTaskId: z.string().min(1).nullable(),
  /**
   * Who started the plan — see {@link PlanInitiator}. Surfaced on the summary (not
   * only the projection) because it is the column `plan list` filters on.
   */
  initiatedBy: PlanInitiatorSchema,
  /**
   * Which mint lane authored the plan — see {@link PlanSource}. On the summary
   * because the index is the scan surface: it says which lane authored each
   * epic, derived the same way the projection derives it (`planSourceOf`), so
   * the two reads cannot disagree.
   */
  source: PlanSourceSchema,
  /**
   * The org member who started the plan (#4477) — see
   * {@link PlanProjectionSchema.owner}; same shape, same required-nullable
   * posture, resolved by the SAME `resolvePlanOwners` batched read so the
   * summary and the detail projection cannot disagree about one plan's owner.
   */
  owner: PlanOwnerSchema.nullable(),
  /**
   * THE LEAD SLICE'S REPO — the lowest-`position` group's `repo_full_name`, the
   * same slice {@link PlanSummarySchema.leadTitle} names, so a card pairs one
   * slice's title with that slice's own repo. It is always one OF
   * {@link PlanSummarySchema.repoFullNames}, never a value outside that set.
   *
   * It used to say "all of a plan's groups share one repo — the plan's target",
   * which was true of every plan that had then been written and never true of the
   * schema: `plan_groups.repo_full_name` is a per-row `notNull` column, and what
   * made one plan mean one repo was a single writer parameter (arc 101 slice 05).
   */
  repoFullName: z.string(),
  /**
   * EVERY repo the plan's slices live in — de-duplicated, sorted, and never
   * empty (a plan with no groups is not a plan; `groupCount` is `positive()` for
   * the same reason).
   *
   * ADDITIVE beside `repoFullName` rather than replacing it, deliberately:
   * `PlanSummary` IS the `GET /v1/plans` wire shape and dashboard pods roll
   * gradually under ArgoCD, so replacing the field would break every pod not yet
   * rolled from the moment the API ships. Retiring `repoFullName` is its own
   * later slice, once no reader wants a single name.
   */
  repoFullNames: z.array(z.string().min(1)).min(1),
  /**
   * Where the goal stands (slice 12) — the SAME {@link planGoalState} over the SAME
   * projection `GET /v1/plans/:planId` serves, so the card and the epic view cannot disagree.
   */
  goalState: PlanGoalStateSchema,
  /** The lowest-`position` slice's title — the plan's headline (no plan-level title exists). */
  leadTitle: z.string(),
  /** How many slices the plan has. */
  groupCount: z.number().int().positive(),
  /**
   * How many slices DELIVERED — {@link PlanGroupDisposition.Landed}: merged, or a
   * `report`/`ops_action` slice that completed — and the progress numerator over
   * `groupCount`. A slice that settled no work is NOT counted here (it shipped
   * nothing); it is `noWorkCount`, although the gate releases both.
   */
  mergedCount: z.number().int().nonnegative(),
  /**
   * How many slices SETTLED NO WORK ({@link PlanGroupDisposition.NoWork}) — their
   * team disproved the premise and nothing shipped. A plan can reach `done` on
   * these alone; this is the number that says so.
   */
  noWorkCount: z.number().int().nonnegative(),
  /**
   * How many slices can no longer reach `merged` at all
   * ({@link PlanGroupDisposition.Stalled}) — their task failed, the run ended
   * with no PR linked, or the linked PR closed unmerged.
   *
   * Its own number rather than folded into `mergedCount`'s complement, because
   * the complement is what lied: `groupCount - mergedCount` reads as "still
   * working" and on prod was 529/583 dead. Slices still genuinely in motion are
   * `groupCount - mergedCount - noWorkCount - stalledCount`; the four are
   * exhaustive over the plan by construction (every group gets exactly one
   * disposition).
   */
  stalledCount: z.number().int().nonnegative(),
  /**
   * How many slices are at {@link PlanGroupStatus.Held} (#5135), AWAITING a
   * `plan approve` — authored tasks that exist but are not yet in the queue. A
   * SUBSET of the in-flight complement above, NOT a fifth exhaustive count
   * beside landed/no_work/stalled: a held group has no dispatched task, so its
   * disposition is already `in_flight`, and `landed + no_work + stalled + in_flight`
   * stays the partition. Its own number because "waiting on a person to press
   * approve" is a different next move than "still working", and
   * {@link planBucket} is the one reader that tells them apart. Tallied from
   * the SAME row set as the other counts (`tallyPlanDispositions`), so the
   * four cannot disagree about one slice.
   *
   * UNRELATED TO {@link PlanStopReasonSchema}'s `kind: 'held'` member, despite
   * sharing the word: that arm names a DISPATCHED task's `hold_reason` (e.g.
   * `repo_not_onboarded`), a different condition entirely. A
   * `PlanGroupStatus.Held` group counted here has no task at all, so it never
   * carries a `hold_reason` and never sets {@link PlanSummarySchema.stopReason}.
   */
  heldCount: z.number().int().nonnegative(),
  /**
   * WHY the plan has stopped — see {@link PlanStopReasonSchema}. Non-null
   * exactly when an open plan has a `stalled` group OR an in-flight group
   * whose DISPATCHED task carries a `hold_reason`; `null` when nothing is
   * stopped. REQUIRED and nullable, the posture `owner` takes above, so a
   * producer that forgets it fails at the read boundary. Derived by
   * `planStopReason` (`./plan-stop-reason.ts`) from the plan's non-landed
   * groups — it covers the task-level holds `stalledCount` cannot, because a
   * hold-blocked slice's disposition stays `in_flight`.
   *
   * NULL FOR A PLAN WAITING ONLY ON APPROVAL: a {@link PlanGroupStatus.Held}
   * group (see {@link PlanSummarySchema.heldCount}) has no dispatched task, so
   * it never carries a `hold_reason` and never contributes a `kind: 'held'`
   * answer here — `heldCount > 0` with every other slice landed or in flight
   * answers `stopReason: null`, not a held reason.
   */
  stopReason: PlanStopReasonSchema.nullable(),
  /**
   * Oldest group's `created_at` (plan birth), ISO-8601.
   *
   * STILL THE GROUPS' timestamps, now that a `plans` row exists with two of its
   * own. Deliberately: `plans.created_at`/`updated_at` are the ROW's writes (birth,
   * and the one status flip), while these two are the plan's WORK moving — a group
   * dispatching, a PR merging, an approve releasing a held slice. Copying activity
   * up onto the plan row would be a denormalization EVERY writer of a group has to
   * keep in step (`insertPlanGroups`, `markPlanGroupMergedByPr`,
   * `approveHeldPlanGroups` and `cancelPlanTasks` today, plus whatever writes a
   * group next), and the first missed bump would sort the list wrong with nothing
   * to catch it.
   */
  createdIso: z.string(),
  /** Newest group's `updated_at` (last plan activity), ISO-8601 — the list's sort key. */
  updatedIso: z.string(),
});
export type PlanSummary = z.infer<typeof PlanSummarySchema>;

// ─── the fleet plans page's tabs (#5135) ─────────────────────────────────────

/**
 * Which tab of the fleet plans page a plan belongs to (#5135). DERIVED per
 * request by {@link planBucket}, never stored: a stored bucket would put every
 * group writer — `insertPlanGroups`, `markPlanGroupMergedByPr`,
 * `approveHeldPlanGroups`, `cancelPlanTasks` and whatever writes a group next —
 * in step with one more column, and the first missed bump would misfile a plan
 * with nothing to catch it. That is the same ruling
 * {@link PlanSummarySchema.createdIso}'s docblock already takes about copying
 * activity up onto the plan row.
 *
 * NOT the plan row's lifecycle: that is {@link PlanStatus} (`open`/`done`/
 * `cancelled`) and it keeps its own field on the summary. The transport filter
 * param is named `bucket` for exactly this reason — a second meaning of
 * `status` on a public contract is a one-way mistake.
 */
export const PlanBucket = {
  /** Stopped by `plan cancel` — the plan row's own `cancelled`. */
  Closed: 'closed',
  /** Finished: the plan row is `done`. */
  Completed: 'completed',
  /** At least one slice can no longer reach `merged` — the plan needs a person. */
  Stalled: 'stalled',
  /** At least one slice is held for a `plan approve` — waiting on a person, not on work. */
  AwaitingApproval: 'awaiting_approval',
  /** Everything else: genuinely still moving. */
  InProgress: 'in_progress',
} as const;
export const PlanBucketSchema = z.enum(PlanBucket);
export type PlanBucket = z.infer<typeof PlanBucketSchema>;

/**
 * The `plan list` free-text search bound (#5135) — a substring of the lead
 * slice's title or the plan id, matched case-insensitively in TS over the
 * fenced rows (never in SQL — the query's template binds no user text). Named
 * so both doors' `.max()` cites the SAME number instead of two literal `200`s
 * drifting apart.
 */
export const PLAN_LIST_Q_MAX = 200;

/**
 * `plan list`'s page-size DEFAULT (#5135), shared by the clamp in
 * `packages/db/src/queries/plan-groups/listing.ts` and every caller that needs
 * to say what an unspecified `perPage` means — the dashboard mock fixtures and
 * the API test double included — so all three cite ONE number instead of three
 * literal `50`s drifting apart.
 */
export const PLAN_LIST_DEFAULT_PER_PAGE = 50;

/**
 * `plan list`'s page-size CEILING (#5135), the same sharing rationale as
 * {@link PLAN_LIST_DEFAULT_PER_PAGE} — a caller that asks for more sees this
 * many, never a literal `100` re-typed at each call site.
 */
export const PLAN_LIST_MAX_PER_PAGE = 100;

/**
 * `plan start`'s prompt CEILING in characters, cited by {@link PlanPromptSchema}
 * (which `PlanStartInputSchema.prompt` in `packages/mcp/src/tools.ts` is, and
 * `POST /v1/plans` parses with) and the dashboard's textarea `maxlength`
 * (`apps/dashboard/src/lib/plan-start.ts`).
 *
 * It IS {@link TASK_PROBLEM_MAX}, never a number of its own: `buildPlanStartEnvelope`
 * (`apps/api/src/deps/plan-start.ts`) copies the ask VERBATIM into the scout task's
 * `envelope.problem`. At 16,000 (until 2026-10-30) the door took asks the problem
 * cannot hold — a 13,661-char ask got a 202 and became a stored task no reader could
 * parse. Raising the problem cap instead is refused: it feeds model-read budgets.
 * `plan-prompt-cap.test.ts` pins the equality.
 */
export const PLAN_PROMPT_MAX_CHARS = TASK_PROBLEM_MAX;

/**
 * `plan start`'s prompt field: trimmed, non-empty, at most
 * {@link PLAN_PROMPT_MAX_CHARS}. `PlanStartInputSchema.prompt` adds only its
 * `.describe()` — the {@link PlanListQSchema} posture. The over-cap refusal names the
 * length, the cap and why, because both doors (REST `errors.fieldErrors.prompt`, the
 * MCP `- prompt:` line) show the caller this sentence and nothing else.
 */
export const PlanPromptSchema = z
  .string()
  .trim()
  .min(1)
  .max(PLAN_PROMPT_MAX_CHARS, {
    error: (iss) =>
      `prompt is ${typeof iss.input === 'string' ? iss.input.length : '?'} characters; plan_start takes at most ${PLAN_PROMPT_MAX_CHARS} (the ask becomes the scout task's problem, capped at TASK_PROBLEM_MAX)`,
  });

/**
 * `plan list`'s `q` bound, shared by REST `?q=` (`apps/api/src/routes/plans.ts`)
 * and the MCP `plan list` action (`packages/mcp/src/army/plan.ts`) — the SAME
 * schema `PlanBucketSchema` already is for `bucket`, closing the one filter
 * that still respelled its bound on both doors instead of citing one source.
 * Non-empty (an empty substring is not a filter — the same reason `q` on other
 * list ports is `min(1)`) and capped at {@link PLAN_LIST_Q_MAX}.
 */
export const PlanListQSchema = z.string().min(1).max(PLAN_LIST_Q_MAX);

/**
 * `plan list`'s `page` bound, shared by REST `?page=` and the MCP `plan list`
 * action — a 1-based page number over the newest-activity-first order. REST's
 * door additionally coerces (`z.coerce.number()`, a query value is always a
 * string) before this schema's own `int().min(1)` validates it; MCP's `page`
 * arrives as a native JSON number, so this schema alone suffices there.
 */
export const PlanListPageNumberSchema = z.number().int().min(1);

/** The {@link PlanSummary} fields {@link planBucket} reads — a summary satisfies it. */
export type PlanBucketInput = Pick<PlanSummary, 'status' | 'stalledCount' | 'heldCount'>;

/**
 * ONE bucket per plan (#5135), first match wins — the server filters the list
 * with it and the dashboard tabs with it later, so the precedence lives in ONE
 * pure function rather than in two screens that could disagree.
 *
 * The precedence, and why each arm outranks the next:
 *
 * 1. `closed` — a stopped epic is out of every working tab, whatever its slices
 *    did before the stop.
 * 2. `completed` — `status === 'done'`. The done writers (`markPlanGroupMergedByPr`,
 *    `reconcilePlansDone`) record `done` for every plan whose slices are all
 *    SATISFIED per `planGroupSatisfiedSql` (`@developerz/db`) — merged, a
 *    completed `report`/`ops_action` slice (and not refused on its current
 *    run), or a `pr` slice whose run settled no work (#5137) — so this bucket
 *    needs no count-based arm of its own. A plan whose last satisfier is not
 *    a merge sees up to one
 *    `cron.dead_dependency` tick (10 minutes) of lag before it reaches this
 *    bucket; until then it reads `in_progress`.
 *    An `ended` plan (R3) is `stalled` when a slice is dead, else `closed` — never
 *    in progress, since nothing in it can move.
 * 3. `stalled` — ANY dead slice (`stalledCount > 0`) outranks held and in-flight
 *    work: a dead slice's dependents wait forever behind the claim gate, so the
 *    plan needs a person even while other slices are genuinely moving.
 * 4. `awaiting_approval` — a held slice (`heldCount > 0`) is waiting on
 *    `plan approve`, a different next move than "still working".
 * 5. `in_progress` — the rest.
 */
export function planBucket(summary: PlanBucketInput): PlanBucket {
  if (summary.status === PlanStatus.Cancelled) return PlanBucket.Closed;
  if (summary.status === PlanStatus.Done) {
    return PlanBucket.Completed;
  }
  // `ended` (R3): nothing can move, so never `in_progress` or `awaiting_approval`. A
  // dead slice still needs a person (`stalled`); otherwise it is out of every tab.
  if (summary.status === PlanStatus.Ended) {
    return summary.stalledCount > 0 ? PlanBucket.Stalled : PlanBucket.Closed;
  }
  if (summary.stalledCount > 0) return PlanBucket.Stalled;
  if (summary.heldCount > 0) return PlanBucket.AwaitingApproval;
  return PlanBucket.InProgress;
}

/**
 * The bucket tallies beside a list page (#5135) — one key per {@link PlanBucket}
 * member plus `all`, so the fleet plans page renders its tab counts without a
 * second read. Every count is over the SAME filtered set the page's `total`
 * divides (initiated_by + repo + q applied, bucket NOT applied), so
 * `sum(buckets) === all` by construction and a tab's number is what its filter
 * would return.
 */
export const PlanListCountsSchema = z.object({
  all: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  stalled: z.number().int().nonnegative(),
  awaiting_approval: z.number().int().nonnegative(),
  in_progress: z.number().int().nonnegative(),
});
export type PlanListCounts = z.infer<typeof PlanListCountsSchema>;

/**
 * The `plan list` envelope (#5135) — numbered OFFSET pages, both transports at
 * once. `total` is the row count of the SELECTED bucket after every filter (the
 * number the pager divides); a page past the end answers `plans: []` with the
 * real `total` and `counts`, never a 404. `per_page` echoes the EFFECTIVE size
 * after the port's clamp, so a caller that asked for 200 sees 100. `repos` is
 * every repo across the account's plans matching `initiated_by` — NOT the
 * filtered set, and never page-bound, so a repo dropdown can be built from any
 * page. The offset engine's accepted cost is stated where the paging decision
 * lives (`packages/db/src/queries/plan-groups/listing.ts`).
 */
export const PlanListPageSchema = z.object({
  plans: z.array(PlanSummarySchema),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1),
  total: z.number().int().nonnegative(),
  counts: PlanListCountsSchema,
  repos: z.array(z.string().min(1)),
});
export type PlanListPage = z.infer<typeof PlanListPageSchema>;
